"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Autoplay, Pagination } from "swiper/modules";
import MainSlider from "@/src/_types/main-slider";
import { Box, Typography } from "@mui/material";
import Image from "next/image";
import { VISUALLY_HIDDEN } from "@/src/utils/visually-hidden";
import Kicker from "@/src/components/common/broadsheet/kicker";
import ActionButton from "@/src/components/common/broadsheet/action-button";
import {
  FONT_DISPLAY,
  FONT_TEXT,
} from "@/src/components/common/broadsheet/tokens";

export default function LeadSlider({ journal }: { journal: any }) {
  const mainSlider: MainSlider[] =
    journal.options?.template_configs?.main_site_slider ?? [];

  if (mainSlider.length === 0) return null;

  return (
    <Box
      id="sliders"
      sx={{
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        "& .swiper": { flex: 1, width: "100%", pb: 1.25 },
        "& .swiper-wrapper": { alignItems: "stretch" },
        "& .swiper-slide": { height: "auto" },
        "& .swiper-pagination-bullet": {
          width: "1.5rem",
          height: "2px",
          borderRadius: 0,
          backgroundColor: "custom.ruleHairline",
          opacity: 1,
        },
        "& .swiper-pagination-bullet-active": {
          backgroundColor: "custom.link",
        },
      }}
    >
      <Swiper
        modules={[Pagination, Autoplay]}
        pagination={{ clickable: true }}
        spaceBetween={20}
        slidesPerView={1}
        autoplay={{ delay: 6000, disableOnInteraction: true }}
        slidesPerGroup={1}
      >
        {mainSlider.map((slider, key) => (
          <SwiperSlide key={key}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                height: "100%",
                minHeight: { md: "21rem" },
              }}
            >
              <Box
                sx={{
                  "& img": {
                    width: "860px",
                    maxWidth: "100%",
                    height: "auto",
                    aspectRatio: "860 / 280",
                    objectFit: "contain",
                  },
                }}
              >
                <Image
                  src={slider.image?.url ?? ""}
                  alt={slider.slide?.label ?? ""}
                  width={860}
                  height={280}
                  preload={key === 0}
                  fetchPriority={key === 0 ? "high" : undefined}
                />
              </Box>

              <Kicker>From the publisher</Kicker>

              <Typography
                component={key === 0 ? "h2" : "p"}
                sx={{
                  m: 0,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  fontSize: { xs: "1rem", md: "1.25rem", lg: "1.375rem" },
                  lineHeight: 1.08,
                  letterSpacing: "0.01em",
                  color: "custom.ink",
                }}
              >
                {slider.slide?.label}
              </Typography>

              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontFamily: FONT_TEXT,
                  fontSize: { xs: "0.9375rem", md: "1rem" },
                  lineHeight: 1.6,
                  color: "custom.inkMuted",
                  maxWidth: "52ch",
                }}
              >
                {slider.slide?.caption}
              </Typography>

              {slider.button?.url ? (
                <Box sx={{ mt: "auto", pt: 0.5 }}>
                  <ActionButton href={slider.button.url}>
                    {slider.button?.label}
                    {slider.slide?.label ? (
                      <Box component="span" sx={VISUALLY_HIDDEN}>
                        {` — ${slider.slide.label}`}
                      </Box>
                    ) : null}
                  </ActionButton>
                </Box>
              ) : null}
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}
